import last from 'lodash/last';
import { StateCreator } from 'zustand';

import { PIXEL_ENVIRONMENTS } from './constants/pixelEnvironments';
import { TRACKING_PIXELS_REQUEST_TIMEOUT } from './constants/trackingPixelsRequestTimeout';
import { TrackingPixelFiresOn } from './enums/TrackingPixelFiresOn';
import { isPixelForPage } from './functions/isPixelForPage';
import { toTrackOptions } from './functions/toTrackOptions';
import { PageViewPayload } from './types/PageViewPayload';
import { TrackingEventPayloads } from './types/TrackingEventPayloads';
import { TrackingPixel } from './types/TrackingPixel';
import { TrackingPixelState } from './types/TrackingPixelState';
import { TrackingPixelStateDependencies } from './types/TrackingPixelStateDependencies';

export const trackingPixelStore =
  ({
    analytics,
    targetEnvironment,
    useApiStore,
    useAttributionStore,
    useLeadChannelStore,
  }: TrackingPixelStateDependencies): StateCreator<TrackingPixelState> =>
  (set, get) => {
    const inFlightRequests = new Map<string, Promise<TrackingPixel[]>>();

    let latestPageView = 0;

    return {
      pixelsByLeadChannel: {},
      sentPageKeys: [],

      computed: {
        get isPixelEnvironment(): boolean {
          return PIXEL_ENVIRONMENTS.includes(targetEnvironment);
        },
      },

      ensureCurrentLeadChannelTrackingPixelsLoaded: async (): Promise<
        TrackingPixel[]
      > => {
        await useLeadChannelStore.getState().ensureLeadChannelLoaded();

        const { lastFetchedLeadChannelCode } = useLeadChannelStore.getState();

        return get().ensureTrackingPixelsLoaded(lastFetchedLeadChannelCode);
      },

      ensureTrackingPixelsLoaded: (
        leadChannelCode?: string | null,
      ): Promise<TrackingPixel[]> => {
        if (!get().computed.isPixelEnvironment) {
          return Promise.resolve([]);
        }

        const key = leadChannelCode ?? '';
        const cachedPixels = get().pixelsByLeadChannel[key];

        if (cachedPixels) {
          return Promise.resolve(cachedPixels);
        }

        if (inFlightRequests.has(key)) {
          return inFlightRequests.get(key);
        }

        const portalHttpRequest = useApiStore.getState().portalApi();

        const promise = portalHttpRequest
          .get<TrackingPixel[] | null>('/ui-configuration/tracking', {
            params: leadChannelCode ? { leadChannelCode } : undefined,
            suppressUnauthorizedRedirect: true,
            timeout: TRACKING_PIXELS_REQUEST_TIMEOUT,
          })
          .then((response): TrackingPixel[] => {
            const pixels = response ?? [];

            set((state) => ({
              pixelsByLeadChannel: {
                ...state.pixelsByLeadChannel,
                [key]: pixels,
              },
            }));

            return pixels;
          })
          // fails closed and isn't cached, so the next send tries again
          .catch((error: unknown): TrackingPixel[] => {
            console.error(error);

            return [];
          })
          .finally((): void => {
            inFlightRequests.delete(key);
          });

        inFlightRequests.set(key, promise);

        return promise;
      },

      sendPageView: async (pageKeys: string[]): Promise<void> => {
        const page = last(pageKeys);

        if (
          !get().computed.isPixelEnvironment ||
          !page ||
          get().sentPageKeys.includes(page)
        ) {
          return;
        }

        latestPageView += 1;

        const pageView = latestPageView;
        const pageUrl = `${window.location.origin}${window.location.pathname}`;

        const pixels =
          await get().ensureCurrentLeadChannelTrackingPixelsLoaded();

        // a page left before its pixels were ready isn't counted, which keeps
        // redirect-only URLs such as /dashboard out of the count
        if (pageView !== latestPageView || get().sentPageKeys.includes(page)) {
          return;
        }

        set((state) => ({ sentPageKeys: [...state.sentPageKeys, page] }));

        const options = toTrackOptions(
          pixels.filter((pixel): boolean => isPixelForPage(pixel, pageKeys)),
        );

        if (!options) {
          return;
        }

        const {
          computed: { partnerRecordUniqueIdentifier },
        } = useAttributionStore.getState();
        const { lastFetchedLeadChannelCode } = useLeadChannelStore.getState();

        const properties: PageViewPayload = {
          leadChannelCode: lastFetchedLeadChannelCode,
          page,
          pageUrl,
          partnerRecordUniqueIdentifier: partnerRecordUniqueIdentifier ?? null,
        };

        analytics.track(TrackingPixelFiresOn.PAGE_VIEW, properties, options);
      },

      sendTrackingEvent: async <Event extends keyof TrackingEventPayloads>(
        event: Event,
        payload: TrackingEventPayloads[Event],
      ): Promise<void> => {
        if (!get().computed.isPixelEnvironment) {
          return;
        }

        const pixels =
          await get().ensureCurrentLeadChannelTrackingPixelsLoaded();

        const options = toTrackOptions(
          pixels.filter(({ firesOn }): boolean => firesOn === event),
        );

        if (options) {
          analytics.track(event, payload, options);
        }
      },
    };
  };
